import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Wishlist() {
  const navigate = useNavigate();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wishlist`);
      setWatches(response.data.listings);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (listingId) => {
    try {
      await axios.post(`${API_URL}/api/wishlist/toggle`, { listing_id: listingId });
      setWatches(watches.filter(w => w.id !== listingId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="wishlist-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2">
            My Wishlist
          </h1>
          <p className="text-muted-foreground">
            {watches.length} {watches.length === 1 ? 'watch' : 'watches'} saved
          </p>
        </div>

        {watches.length === 0 ? (
          <Card className="p-12 text-center border-border bg-card" data-testid="wishlist-empty-state">
            <p className="text-muted-foreground mb-4">Your wishlist is empty</p>
            <Button onClick={() => navigate('/catalog')} data-testid="wishlist-browse-button">
              Browse Watches
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="wishlist-grid">
            {watches.map((watch) => (
              <Card
                key={watch.id}
                className="group relative rounded-xl border border-[#1A2232] bg-[#0F1B2D]/60 shadow-[0_2px_24px_rgba(0,0,0,0.35)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.45)] transition-shadow"
                data-testid="wishlist-product-card"
              >
                <button
                  onClick={() => handleRemove(watch.id)}
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-background/80 hover:bg-background transition-colors"
                  data-testid="wishlist-remove-button"
                >
                  <X className="h-4 w-4" />
                </button>
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/watch/${watch.id}`)}
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-t-xl bg-[#0B1220]">
                    <img
                      src={watch.images[0] || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=800'}
                      alt={`${watch.brand} ${watch.model}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <h3 className="font-display text-base text-foreground truncate">
                          {watch.brand} {watch.model}
                        </h3>
                        <p className="text-xs text-muted-foreground truncate">
                          {watch.ref_number}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-primary font-semibold">
                        {formatPrice(watch.price, watch.currency)}
                      </span>
                      <Badge variant="outline" className="text-[10px] bg-transparent border-primary/30 text-foreground">
                        {watch.condition}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}